import clsx from "clsx";
import { Note } from "@knowt/syncing/graphql/schema";
import { useMounted } from "@/hooks/useMounted";
import { useNotePageContextSelector } from "@/components/NotePage/hooks/NotePageContext";
import { FolderWithItems } from "@/components/NotePage/types";
import FolderContent from "./FolderContent";
import { FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import { Pin, Sparkle, Tag } from "lucide-react";
import { iconSizes } from "@/utils/iconProps";
import LinkWrapper from "@/components/wrappers/Link";
import CircularRectTextButton from "@/components/CircularButton/styled/CircularRectTextButton";
import { useMemo } from "react";
import { UNTITLED } from "@knowt/syncing/utils/dataCleaning";
import Image from "next/image";
import { ASSETS_URL } from "@/config/deployConstants";
import { timeDeltaFromNow } from "@knowt/syncing/utils/dateTimeUtils";
import { isDarkColor } from "@knowt/syncing/utils/genericUtils";
import { useClassNotes } from "@knowt/syncing/hooks/notes/useNotes";
import Circular from "@/components/Circular";
import { useAllBookmarks } from "@knowt/syncing/hooks/bookmarks/useBookmarks";
import CircularFilledIcon from "@/components/CircularButton/styled/CircularFilledIcon";
import { getNoteUrl } from "@/utils/url";

const ClassTag = ({ courseName, courseColor }: { courseName: string; courseColor: string }) => {
    return (
        <FlexRowAlignCenter
            style={{
                backgroundColor: courseColor,
                padding: "0.5rem 1rem",
                borderRadius: "1rem",
                width: "fit-content",
                height: "3rem",
            }}>
            <span
                className={clsx(["secondaryTextBold1", "ellipsisText"])}
                style={{
                    color: isDarkColor(courseColor) ? themeColors.pureWhite : themeColors.pureBlack,
                    fontSize: "1rem",
                    maxWidth: "6rem",
                }}
                data-testid="auto-id-99c07674-7621-4578-9c78-84ba577970ad">
                {courseName}
            </span>
        </FlexRowAlignCenter>
    );
};

const TagsTag = ({ tagsLength }: { tagsLength: number }) => {
    if (tagsLength === 0) return null;

    return (
        <FlexRowAlignCenter
            style={{
                gap: "0.4rem",
                backgroundColor: themeColors.neutral1,
                padding: "0.6rem 1.2rem",
                borderRadius: "1rem",
                height: "3rem",
                width: "fit-content",
            }}>
            <Tag size={13} />
            <span className="secondaryText2" data-testid="auto-id-84f89ec6-ecfa-4269-9b64-0e5f34ea2bc7">
                {tagsLength} Tag{tagsLength !== 1 ? "s" : ""}
            </span>
        </FlexRowAlignCenter>
    );
};

const ContentCard = ({
    children,
    disabled,
    style,
}: { children: React.ReactNode; disabled?: boolean; style?: React.CSSProperties }) => {
    return (
        <CircularRectTextButton
            component={"div"}
            style={{
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                padding: "1rem 1rem",
                borderRadius: "1.5rem",
                backgroundColor: themeColors.background,
                minHeight: "6.8rem",
                ...style,
            }}
            disabled={disabled}>
            {children}
        </CircularRectTextButton>
    );
};

export const FilesStructure = () => {
    const mounted = useMounted();

    const notes = useNotePageContextSelector(state => state.explorerNotes);
    const folders = useNotePageContextSelector(state => state.explorerFolders);

    const getFoldersWithNestedFiles = (): {
        files: Note[];
        folders: FolderWithItems[];
    } => {
        if (!folders || !notes) return { folders: [], files: [] };

        const getDirectChildren = (folderId: string | null): { files: Note[]; folders: FolderWithItems[] } => {
            const childrenFiles = Object.values(notes).filter(note => note.folderId === folderId);
            const childrenFolders = Object.values(folders).filter(folder => folder.parentId === folderId);

            return {
                files: childrenFiles,
                folders: childrenFolders.map(folder => ({ ...folder, items: getDirectChildren(folder.folderId) })),
            };
        };

        return getDirectChildren(null);
    };

    if (!mounted) {
        return null;
    }

    return (
        <FolderContent
            isNestedFolder={false}
            files={getFoldersWithNestedFiles().files}
            folders={getFoldersWithNestedFiles().folders}
        />
    );
};

const NotesExplorer = ({ notes }: { notes: (Note & { bookmarked?: boolean })[] }) => {
    const note = useNotePageContextSelector(state => state.note);
    const userClasses = useNotePageContextSelector(state => state.userClasses);

    const explorerFilters = useNotePageContextSelector(state => state.explorerFilters);

    const filteredNotes = useMemo(() => {
        if (!explorerFilters) {
            return notes;
        }

        const filteredNotes = notes
            .filter(note => {
                const matchesFolder = !explorerFilters.folderId || note.folderId === explorerFilters.folderId;
                const matchesTag =
                    !explorerFilters.tag ||
                    note.exam_v2 === explorerFilters.tag ||
                    note.examSection === explorerFilters.tag;
                const matchesClass = !explorerFilters.classId || note.classId === explorerFilters.classId;
                return matchesFolder && matchesTag && matchesClass;
            })
            .sort((a, b) => Number(b.updated) - Number(a.updated));

        return filteredNotes;
    }, [notes, explorerFilters, note]);

    return (
        <FlexColumn style={{ gap: spacing.XS_2 }}>
            {filteredNotes?.map(fNote => (
                <LinkWrapper
                    key={fNote.noteId}
                    href={getNoteUrl({
                        noteId: fNote.noteId,
                        title: fNote.title,
                    })}>
                    <ContentCard
                        disabled={fNote.noteId === note.noteId}
                        style={{
                            backgroundColor:
                                fNote.noteId === note.noteId ? themeColors.neutral1 : themeColors.background,
                        }}>
                        <FlexRowAlignCenter style={{ gap: spacing.XS, marginBottom: "1.2rem" }}>
                            {fNote.bookmarked ? (
                                <CircularFilledIcon
                                    Icon={Pin}
                                    IconProps={{ size: 15, fill: themeColors.pureBlack }}
                                    style={{
                                        width: "2.4rem",
                                        height: "2.4rem",
                                        opacity: 1,
                                    }}
                                    primaryBg={themeColors.primary3}
                                    primaryColor={themeColors.pureBlack}
                                    disabled
                                />
                            ) : (
                                <Image
                                    src={`${ASSETS_URL}/images/note-icon.svg`}
                                    alt="note icon"
                                    width={iconSizes.SM}
                                    height={iconSizes.SM}
                                />
                            )}
                            <span
                                className="secondaryTextBold1"
                                data-testid="auto-id-e78682cc-7765-4f9b-a0e3-106bfca4daae">
                                {fNote.title?.length ? fNote.title : UNTITLED}
                            </span>
                        </FlexRowAlignCenter>

                        <FlexRowAlignCenter style={{ gap: "0.5rem" }}>
                            {fNote.isAI && (
                                <Circular
                                    className="secondaryText2"
                                    style={{
                                        gap: "0.4rem",
                                        background: themeColors.aiGradient,
                                        padding: "0.6rem 1.2rem",
                                        borderRadius: "1rem",
                                        height: "3rem",
                                        color: themeColors.pureBlack,
                                    }}>
                                    <Sparkle size={"1.3rem"} color={themeColors.ai} fill={themeColors.ai} />
                                    AI
                                </Circular>
                            )}
                            {fNote.classId && userClasses?.[fNote.classId] && (
                                <ClassTag
                                    courseName={userClasses?.[fNote.classId].name}
                                    courseColor={userClasses?.[fNote.classId].color}
                                />
                            )}
                            <TagsTag
                                tagsLength={
                                    (fNote.tags?.length || 0) + (fNote.subject ? 1 : 0) + (fNote.exam_v2 ? 1 : 0)
                                }
                            />
                            <Circular
                                className="secondaryText2"
                                style={{
                                    gap: "0.4rem",
                                    background: themeColors.neutral1,
                                    padding: "0.6rem 1.2rem",
                                    borderRadius: "1rem",
                                    height: "3rem",
                                }}>
                                {timeDeltaFromNow(+(fNote.updated ?? 0) * 1000, true)} ago
                            </Circular>
                        </FlexRowAlignCenter>
                    </ContentCard>
                </LinkWrapper>
            ))}
        </FlexColumn>
    );
};

const Explorer = ({ classId, filterQuery }: { classId?: string; filterQuery?: string }) => {
    const note = useNotePageContextSelector(state => state.note);
    const notes = useNotePageContextSelector(state => state.explorerNotes);

    const { bookmarks } = useAllBookmarks();

    const { classNotes } = useClassNotes({
        classId: note?.classId || "",
        sectionId: null,
        isEnabled: !!note?.classId,
    });

    const notesToShow = useMemo(() => {
        if (!classId) return Object.values(notes || {});
        return Object.values(classNotes || {});
    }, [classId, notes, classNotes]);

    const filteredNotes = useMemo(() => {
        if (!filterQuery) {
            return notesToShow.map(note => ({
                ...note,
                bookmarked: bookmarks?.[note.noteId] !== undefined,
            }));
        }
        return notesToShow
            .filter(note => note.title?.toLowerCase().includes(filterQuery.toLowerCase()))
            .map(note => ({
                ...note,
                bookmarked: bookmarks?.[note.noteId] !== undefined,
            }));
    }, [filterQuery, notesToShow, bookmarks]);

    return (
        <div
            className={clsx("scrollbar thin")}
            style={{ flex: 1, overflow: "hidden auto", height: "100%", paddingBottom: "1.2rem" }}
            data-testid="auto-id-3a29fc98-120d-4131-a450-262092b95192">
            <NotesExplorer notes={filteredNotes} />
        </div>
    );
};

export default Explorer;
